import React from "react";
import { Box, Checkbox, CircularProgress, FormControlLabel, Tooltip, Typography } from "@mui/material";
import { AppDialog } from "../AppDialog";
import { ReportLockPrompt } from "../../hooks/useWorklogWeekApproval";
import { WorklogWeek } from "../../utils/WorklogWeekUtils";
import { filtersBarStyle, Token } from "../../theme";

/** Same height with or without weeks, so the table below does not jump when the weeks arrive. */
export const WEEK_APPROVAL_BAR_HEIGHT = 48;

interface Props {
  weeks: WorklogWeek[];
  busyWeekKey?: string;
  onToggle: (week: WorklogWeek, approve: boolean) => void;
  lockPrompt?: ReportLockPrompt;
  onDismissLockPrompt: () => void;
  onConfirmOpenReport: () => void;
}

const monthYearOf = (week: WorklogWeek) => new Date(week.year, week.month, 1).toLocaleString("en-US", { month: "long", year: "numeric" });

export const WorklogWeekApproval: React.FC<Props> = ({
  weeks,
  busyWeekKey,
  onToggle,
  lockPrompt,
  onDismissLockPrompt,
  onConfirmOpenReport,
}) => {
  const isBlockedPrompt = lockPrompt?.kind === "blocked";

  return (
    <>
      <Box
        role="group"
        aria-label="Weekly approval"
        style={{
          ...filtersBarStyle(),
          flexWrap: "nowrap",
          overflowX: "auto",
          whiteSpace: "nowrap",
          height: WEEK_APPROVAL_BAR_HEIGHT,
          padding: "0 12px",
          gap: "16px",
        }}
      >
        <Typography
          component="span"
          style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".08em", fontWeight: 600, color: Token.textTertiary }}
        >
          Approved weeks
        </Typography>
        {weeks.map((week) => {
          const busy = busyWeekKey === week.key;
          return (
            <Tooltip
              key={week.key}
              title={
                week.approved
                  ? "Approved. Untick it to unapprove the week and edit its days."
                  : "Not approved. Tick it to approve the week."
              }
            >
              <FormControlLabel
                style={{ margin: 0 }}
                disabled={!!busyWeekKey}
                label={<span style={{ fontSize: 13, color: Token.textPrimary }}>{week.label}</span>}
                control={
                  busy ? (
                    <CircularProgress size={18} style={{ margin: 9 }} aria-label={`Updating ${week.label}`} />
                  ) : (
                    <Checkbox
                      size="small"
                      color="default"
                      checked={week.approved}
                      onChange={(event) => !busyWeekKey && onToggle(week, event.target.checked)}
                      inputProps={{ "aria-label": `Approved ${week.label}` }}
                    />
                  )
                }
              />
            </Tooltip>
          );
        })}
      </Box>

      {/* A closed report. Not an admin: it can only be told. Admin: it has to be confirmed that the report is going to be opened. */}
      <AppDialog
        open={!!lockPrompt}
        onClose={onDismissLockPrompt}
        title={isBlockedPrompt ? "Report closed" : "Open the report"}
        hideCancel={isBlockedPrompt}
        content={
          <Typography>
            {lockPrompt &&
              (isBlockedPrompt
                ? `The report of ${monthYearOf(lockPrompt.week)} is closed, so the week ${lockPrompt.week.label} cannot be ${
                    lockPrompt.approve ? "approved" : "unapproved"
                  }. Only an admin can re-open it.`
                : `The report of ${monthYearOf(lockPrompt.week)} is closed. To ${
                    lockPrompt.approve ? "approve" : "unapprove"
                  } the week ${lockPrompt.week.label} it is going to be opened first. Remember to close it again from the Worklog report when you are done. Do you want to continue?`)}
          </Typography>
        }
        buttons={
          isBlockedPrompt
            ? [{ label: "Understood", onClick: onDismissLockPrompt }]
            : [{ label: "Open report and continue", onClick: onConfirmOpenReport }]
        }
      />
    </>
  );
};
